import { useMemo } from "react"
import { useParams } from "react-router-dom"
import { useTranslation } from "providers/i18n"
import {
  Box,
  Card,
  CardHeader,
  CardContent,
  CardMedia,
  IconButton,
  List,
  Typography,
  useTheme,
  Button,
} from "@mui/material"
import LanguageIcon from "@mui/icons-material/Language"

import { useEventDetail } from "hooks/event/useEventDetail"

import { ShareIconButton } from "components/Button"
import { CalendarIcon, LocationIcon } from "components/icons"
import { Head } from "components/Head"
import { InfoListItem, InfoListItemWithNavigation } from "components/ListItem"
import { LocationInfo } from "components/domains/features/LocationInfo"
import { Carousel, CarouselItem } from "components/Carousel"
import { ScrollTrigger } from "components/ScrollTrigger"

import { getIcon } from "utils/icon"
import { PageLayout } from "extensions/iconsiam/layouts/PageLayout"
import { Header } from "extensions/iconsiam/components/Header"
import { HeaderBackButton } from "extensions/iconsiam/components/Header/HeaderBackButton"
import { HeaderSearchButton } from "extensions/iconsiam/components/Header/HeaderSearchButton"

export const EventDetail = ({ Layout = PageLayout }) => {
  const params = useParams()
  const { eventId } = params
  const { t } = useTranslation()
  const { event, navigateToLocation, openWebsiteLink } = useEventDetail(eventId)
  const theme = useTheme()
  const styleOverrides = theme.components[`VuiDetailCard`]?.styleOverrides || {}
  const {
    card,
    infoListItem,
    infoListItemWithNavigation,
    cardContent,
    cardHeader,
    cardHeaderAction,
    cardHeaderActionButton,
    viewLocationContainer,
    viewLocationButton,
    scrollTriggerWrapper,
    cardContentPrimaryText,
    cardContentSecondaryText,
    galleryCarouselWrapper,
    galleryCarousel,
    galleryCarouselItem,
  } = styleOverrides

  const locationIcon = useMemo(
    () => getIcon(theme?.assets?.locationIcon) || <LocationIcon />,
    [theme]
  )
  const calendarIcon = useMemo(
    () => getIcon(theme?.assets?.calendarIcon) || <CalendarIcon />,
    [theme]
  )

  const direction = theme.direction

  if (!event) return null

  return (
    <>
      <Head title={`${t(event.name)} (${t("Event Detail")})`} />
      <Layout
        header={
          <Header
            backButton={<HeaderBackButton />}
            searchButton={<HeaderSearchButton />}
          />
        }
      >
        <Card elevation={0} sx={card}>
          {event.allImages && (
            <Box sx={galleryCarouselWrapper}>
              <Carousel
                options={{
                  type: "loop",
                  padding: 0,
                  gap: 8,
                  pagination: true,
                  autoplay: true,
                  direction: direction,
                }}
                sx={galleryCarousel}
              >
                {event.allImages.map(({ id, url }) => {
                  return (
                    <CarouselItem key={`slide-${id}`} sx={galleryCarouselItem}>
                      <CardMedia
                        component="img"
                        sx={galleryCarouselItem}
                        src={url as string}
                      />
                    </CarouselItem>
                  )
                })}
              </Carousel>
            </Box>
          )}
          <CardHeader
            title={t(event.name)}
            action={
              <Box sx={cardHeaderAction}>
                {!!event.websiteLink && (
                  <IconButton
                    className="website-button"
                    color="primary"
                    // * URL should be prepended with protocol (e.g. "https://") for the URL to be replaced
                    onClick={openWebsiteLink}
                    sx={cardHeaderActionButton}
                  >
                    <LanguageIcon />
                  </IconButton>
                )}
                <ShareIconButton
                  className="share-button"
                  url={window.location.href}
                  sx={cardHeaderActionButton}
                />
              </Box>
            }
            sx={cardHeader}
          />
          {event.hasLocation && (
            <Box sx={viewLocationContainer}>
              <Button
                variant="contained"
                startIcon={locationIcon}
                onClick={navigateToLocation}
                sx={viewLocationButton}
              >
                {t("View Location")}
              </Button>
            </Box>
          )}
          {event.showInfo && (
            <List>
              {!event.showAlways && (
                <InfoListItem
                  sx={infoListItem}
                  icon={calendarIcon}
                  primary={event.durationDate}
                  secondary={event.openingText}
                  direction="ltr"
                />
              )}
              {event.hasLocation && (
                <InfoListItemWithNavigation
                  sx={infoListItemWithNavigation}
                  icon={locationIcon}
                  primary={t("Navigate")}
                  secondary={
                    <LocationInfo feature={event.feature} component="span" />
                  }
                  onClick={navigateToLocation}
                />
              )}
            </List>
          )}
          <CardContent sx={cardContent}>
            <Typography
              variant="body1"
              gutterBottom
              sx={cardContentPrimaryText}
            >
              {t("Event Detail")}
            </Typography>
            <Typography
              variant="body2"
              sx={cardContentSecondaryText}
              dangerouslySetInnerHTML={{
                __html: event.descriptionText,
              }}
            ></Typography>
          </CardContent>
        </Card>
      </Layout>
      {event.hasLocation && (
        <ScrollTrigger scrollOption={{ threshold: 750 }}>
          <Box sx={scrollTriggerWrapper}>
            <Box sx={viewLocationContainer}>
              <Button
                variant="contained"
                startIcon={locationIcon}
                onClick={navigateToLocation}
                sx={viewLocationButton}
              >
                {t("View Location")}
              </Button>
            </Box>
          </Box>
        </ScrollTrigger>
      )}
    </>
  )
}
